import { v4 as uuidv4 } from 'uuid';
import { createJSONStorage } from 'zustand/middleware';
import { createStoreWithPersistAndSubscribe } from '@frontend/store';
import { AIChatbotResponse, MessageFeedback } from '../types';

interface CopilotChatSessionStore {
  sessionsMap: Record<string, { messages: AIChatbotResponse[] }>;
  addMessage: (sessionId: string, message: AIChatbotResponse) => void;
  getMessages: (sessionId: string) => AIChatbotResponse[];
  initSession: () => string;
  updateFeedback: (config: { sessionId: string; messageId: string; feedback: MessageFeedback }) => void;
}

export const useCopilotChatSessionStore = createStoreWithPersistAndSubscribe<CopilotChatSessionStore>(
  (set, get) => ({
    sessionsMap: {},

    addMessage: (sessionId: string, message: AIChatbotResponse) => {
      set((state) => {
        const session = state.sessionsMap[sessionId] || { messages: [] };
        return {
          sessionsMap: {
            ...state.sessionsMap,
            [sessionId]: {
              messages: [...session.messages, message],
            },
          },
        };
      });
    },

    getMessages: (sessionId: string) => {
      const session = get().sessionsMap[sessionId];
      return session ? session.messages : [];
    },

    initSession: () => {
      const sessionId = uuidv4();
      set((state) => ({
        sessionsMap: {
          ...state.sessionsMap,
          [sessionId]: { messages: [] },
        },
      }));
      return sessionId;
    },

    updateFeedback: ({ sessionId, messageId, feedback }) => {
      set((state) => {
        const session = state.sessionsMap[sessionId];
        if (!session) return state;

        const updatedMessages = session.messages.map((msg) => (msg.id === messageId ? { ...msg, feedback } : msg));

        return {
          sessionsMap: {
            ...state.sessionsMap,
            [sessionId]: {
              messages: updatedMessages,
            },
          },
        };
      });
    },
  }),
  {
    name: 'weave-copilot-chat-session-store',
    storage: createJSONStorage(() => localStorage),
  },
  { name: 'weave-copilot-chat-session-store', trace: true }
);
